/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { MaterialitySurveyModelMinData } from '@apps/materiality-tracker/api/materiality-assessment';
import { getAssessmentTypeLabel } from '@apps/materiality-tracker/utils';
import { CustomMetricCardProps } from '@features/custom-metrics/CustomMetricGroups';
import { CardGridButtonProps } from '@features/survey-scope/CardGrid';
import { getCardIcon } from '@features/survey-scope/CustomMetricCards';
import { QUESTION } from '@constants/terminology';
import { AccessType, MetricGroup } from '@g17eco/types/metricGroup';
import { DATE, formatDateUTC } from '@utils/date';
import config from '../../../../config';
import { AssessmentType } from '../../../../types/survey';
import { sortEffectiveDateDesc } from '@utils/sort';

// Match groupName in the format "Date (Assessment type)", capturing 'Date' as title and 'Assessment type' as subtitle
const getCardTitleAndSubtitle = (metricGroup: MetricGroup) => {
  const match = metricGroup.groupName.match(/^(.+?)\s*\((.+?)\)$/);
  if (match) {
    return {
      title: match[1],
      subtitle: match[2],
    };
  }
  return {
    title: metricGroup.groupName,
    subtitle: metricGroup.parentId ? undefined : getAssessmentTypeLabel(metricGroup.survey),
  };
};

const sortMetricGroupsByEffectiveDate = (a: MetricGroup, b: MetricGroup) => {
  if (a.survey && b.survey) {
    return sortEffectiveDateDesc(a.survey, b.survey);
  }
  return 0;
};

export interface MetricGroupsProps {
  metricGroups: MetricGroup[];
  getButtons: (metricGroup: MetricGroup) => CardGridButtonProps[];
}

export const getCustomMetricGroups = ({ metricGroups, getButtons }: MetricGroupsProps) => {
  const cards: CustomMetricCardProps[] = [...metricGroups]
    .sort(sortMetricGroupsByEffectiveDate)
    .map((metricGroup: MetricGroup) => {
      const { title, subtitle } = getCardTitleAndSubtitle(metricGroup);
      const card: CustomMetricCardProps = {
        key: `scope-cardgriditem-metricgroup-${metricGroup._id}`,
        title: <span>{title}</span>,
        sortTitle: metricGroup.groupName,
        displayGridSubtitles: true,
        subtitle,
        description: metricGroup.description ?? '',
        icon: getCardIcon(metricGroup),
        unitCount: metricGroup.universalTrackers?.length ?? 0,
        unitName: QUESTION.CAPITALIZED_PLURAL,
        buttons: getButtons(metricGroup),
        scopeTag: metricGroup._id,
        inScope: false,
        isPartial: false,
        accessType: AccessType.Custom,
      };
      return card;
    });

  return cards;
};

export const initializeMetricGroupFromSurvey = (survey: MaterialitySurveyModelMinData): MetricGroup => ({
  _id: `metric-group-${survey._id}`,
  groupName: formatDateUTC(survey.effectiveDate, DATE.MONTH_YEAR),
  groupData: {
    icon: `${config.media.logosBaseUrl}/materiality_tracker_logo.svg`,
  },
  initiativeId: survey.initiativeId,
  accessType: AccessType.Custom,
  survey: {
    ...survey,
      /** @todo: apply migration for materiality surveys then change this to required */
    assessmentType: survey.assessmentType ?? AssessmentType.FinancialMateriality,
  },
  updated: new Date().toISOString(),
});
